import { PLANS } from "../data/catalog";
import { normalize } from "./format";
import { LANDING_PLANS_HREF, LandingCatalogProduct } from "./landingPlanTypes";

/**
 * Catalog slugs keyed by normalized plan name.
 *
 * A later duplicate normalized name overwrites the earlier one. Only `name`
 * and `slug` are read from each static plan.
 */
const slugByNormalizedName = new Map<string, unknown>(
  PLANS.map((plan) => [normalize(plan.name), plan.slug]),
);

/**
 * Catalog slug when the normalized title matches, otherwise the normalized
 * title with spaces as hyphens.
 *
 * A title that is not a string is "". A catalog slug is returned unchanged,
 * including "".
 */
export function productSlug(title: string | null | undefined): string {
  if (typeof title !== "string") {
    return "";
  }

  const key = normalize(title);
  if (!slugByNormalizedName.has(key)) {
    return key.replace(/\s+/g, "-");
  }

  const slug = slugByNormalizedName.get(key);
  return typeof slug === "string" ? slug : "";
}

/**
 * `/planos?plano=` plus the slug when that string still parses as the slug.
 *
 * `/planos` reads `plano` with `URLSearchParams`. A raw `&`, `+`, or `#`,
 * or a `%` that the parser would decode, would open a different plan, so
 * those slugs are encoded. Parentheses and ordinary hyphens stay as written.
 */
function hrefWithSlug(slug: string): string {
  const literal = `${LANDING_PLANS_HREF}?plano=${slug}`;
  try {
    const parsed = new URL(literal, "http://localhost").searchParams.get("plano");
    if (parsed === slug) return literal;
  } catch {
    // The slug cannot sit in the query unencoded.
  }
  const params = new URLSearchParams();
  params.set("plano", slug);
  return `${LANDING_PLANS_HREF}?${params.toString()}`;
}

/**
 * Href keeps the query only when that slug is non-empty and unique in the
 * loaded payload.
 *
 * Every object row counts, including disabled and zero-price products. A
 * non-array payload counts as no rows. An empty slug never takes a query,
 * and it does not collide with a different slug.
 */
export function detailsHref(
  slug: string,
  products: readonly LandingCatalogProduct[] | null | undefined,
): string {
  if (slug === "") {
    return LANDING_PLANS_HREF;
  }
  if (countProductsWithSlug(slug, products) !== 1) {
    return LANDING_PLANS_HREF;
  }
  return hrefWithSlug(slug);
}

function countProductsWithSlug(
  slug: string,
  products: readonly LandingCatalogProduct[] | null | undefined,
): number {
  if (!Array.isArray(products)) {
    return 0;
  }

  let count = 0;
  for (const product of products) {
    if (typeof product !== "object" || product === null) {
      continue;
    }
    if (productSlug(product.title) === slug) {
      count += 1;
    }
  }
  return count;
}
